import { FC, useState, useEffect } from "react"
import { Card, CardContent } from "../../../components/ui"
import { ChevronDown, ChevronUp, AlertTriangle } from "lucide-react"

type PrintSettings = {
  labelWidth: number
  labelHeight: number
  labelsPerPage: number
  pageMarginTop: number
  pageMarginLeft: number
  pageSize: 'letter' | 'a4' | 'legal'
  orientation: 'portrait' | 'landscape'
}

type BarcodePrintPreviewProps = {
  settings: PrintSettings
}

export const BarcodePrintPreview: FC<BarcodePrintPreviewProps> = ({ settings }) => {
  // Determine initial state based on screen size
  const [isExpanded, setIsExpanded] = useState(false)
  const [showWarning, setShowWarning] = useState(false)

  useEffect(() => {
    // Check if desktop (lg breakpoint is 1024px in Tailwind)
    const isDesktop = window.innerWidth >= 1024
    setIsExpanded(isDesktop)
  }, [])

  // Page dimensions in inches
  const pageDimensions = {
    letter: { width: 8.5, height: 11 },
    a4: { width: 8.27, height: 11.69 },
    legal: { width: 8.5, height: 14 }
  }

  const pageDim = pageDimensions[settings.pageSize]
  const pageWidth = settings.orientation === 'portrait' ? pageDim.width : pageDim.height
  const pageHeight = settings.orientation === 'portrait' ? pageDim.height : pageDim.width

  // Calculate labels per row and column (same logic as PDF generation)
  let labelsPerRow: number
  let labelsPerColumn: number
  let actualLabelsPerPage: number

  actualLabelsPerPage = settings.labelsPerPage
  const requestedLabelsPerPage = settings.labelsPerPage

  if (actualLabelsPerPage === 1) {
    labelsPerRow = 1
    labelsPerColumn = 1
  } else {
    // Calculate maximum labels that can fit per row based on page width
    const availableWidth = pageWidth - (settings.pageMarginLeft * 2)
    const maxLabelsPerRow = Math.floor(availableWidth / settings.labelWidth)

    // Calculate maximum labels that can fit per column based on page height
    const availableHeight = pageHeight - (settings.pageMarginTop * 2)
    const maxLabelsPerColumn = Math.floor(availableHeight / settings.labelHeight)

    // Try to make a reasonably square grid, but respect page width constraints
    const idealLabelsPerRow = Math.ceil(Math.sqrt(actualLabelsPerPage))
    labelsPerRow = Math.min(idealLabelsPerRow, maxLabelsPerRow)
    labelsPerColumn = Math.ceil(actualLabelsPerPage / labelsPerRow)

    // Verify the layout doesn't exceed page height
    if (labelsPerColumn > maxLabelsPerColumn) {
      labelsPerColumn = maxLabelsPerColumn
      actualLabelsPerPage = labelsPerRow * labelsPerColumn
    }
  }

  // Check if labels per page was adjusted
  useEffect(() => {
    setShowWarning(actualLabelsPerPage !== requestedLabelsPerPage)
  }, [actualLabelsPerPage, requestedLabelsPerPage])

  // Calculate preview scale (fit to 300px height)
  const previewHeight = 300
  const scale = previewHeight / pageHeight
  const previewWidth = pageWidth * scale

  // Generate label positions
  const labelPositions = []
  for (let i = 0; i < actualLabelsPerPage; i++) {
    const row = Math.floor(i / labelsPerRow)
    const col = i % labelsPerRow

    let x: number
    let y: number

    if (actualLabelsPerPage === 1) {
      // Center the label on the page
      x = (pageWidth - settings.labelWidth) / 2
      y = (pageHeight - settings.labelHeight) / 2
    } else {
      // Calculate available space for the grid
      const availableWidth = pageWidth - (settings.pageMarginLeft * 2)
      const availableHeight = pageHeight - (settings.pageMarginTop * 2)

      // Calculate total space needed for all labels
      const totalLabelsWidth = labelsPerRow * settings.labelWidth
      const totalLabelsHeight = labelsPerColumn * settings.labelHeight

      // Calculate spacing to distribute labels evenly
      const horizontalSpacing = labelsPerRow > 1
        ? (availableWidth - totalLabelsWidth) / (labelsPerRow - 1)
        : 0
      const verticalSpacing = labelsPerColumn > 1
        ? (availableHeight - totalLabelsHeight) / (labelsPerColumn - 1)
        : 0

      // Position labels with even spacing
      x = settings.pageMarginLeft + (col * (settings.labelWidth + horizontalSpacing))
      y = settings.pageMarginTop + (row * (settings.labelHeight + verticalSpacing))
    }

    labelPositions.push({
      x: x * scale,
      y: y * scale,
      width: settings.labelWidth * scale,
      height: settings.labelHeight * scale
    })
  }

  return (
    <Card className="bg-white dark:bg-neutral800 border-neutral300 dark:border-neutral600">
      <CardContent className="pt-4">
        <div className="space-y-2">
          {/* Collapsible Header */}
          <div
            className="flex items-center justify-between cursor-pointer select-none"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-neutral700 dark:text-neutral300">
                Page Preview
              </h3>
              <p className="text-xs text-neutral500 dark:text-neutral400">
                {actualLabelsPerPage} label{actualLabelsPerPage !== 1 ? 's' : ''} per page
                {showWarning && (
                  <span className="text-warning600 dark:text-warning500 ml-1">
                    (adjusted from {requestedLabelsPerPage})
                  </span>
                )}
              </p>
            </div>
            {showWarning && (
              <AlertTriangle className="text-warning600 dark:text-warning500 flex-shrink-0" width={16} height={16} />
            )}
            {isExpanded ? (
              <ChevronUp className="text-neutral600 dark:text-neutral400" width={20} height={20} />
            ) : (
              <ChevronDown className="text-neutral600 dark:text-neutral400" width={20} height={20} />
            )}
          </div>

          {/* Warning Message */}
          {showWarning && (
            <div className="bg-warning50 dark:bg-warning900/20 border border-warning500 dark:border-warning700 rounded-md p-2 text-xs">
              <p className="text-warning900 dark:text-warning300 font-medium">
                Labels adjusted to fit page dimensions
              </p>
              <p className="text-warning700 dark:text-warning400 mt-1">
                Only {actualLabelsPerPage} of {requestedLabelsPerPage} requested labels fit on the page.
                Reduce label size or increase page margins to fit more labels.
              </p>
            </div>
          )}

          {/* Collapsible Content */}
          {isExpanded && (
            <div className="flex justify-center pt-2">
              <div
                className="relative bg-white border-2 border-neutral300 shadow-md"
                style={{
                  width: `${previewWidth}px`,
                  height: `${previewHeight}px`
                }}
              >
                {/* Margin guides */}
                <div
                  className="absolute border border-dashed border-neutral400 opacity-30"
                  style={{
                    left: `${settings.pageMarginLeft * scale}px`,
                    right: `${settings.pageMarginLeft * scale}px`,
                    top: `${settings.pageMarginTop * scale}px`,
                    bottom: `${settings.pageMarginTop * scale}px`
                  }}
                />

                {/* Label rectangles */}
                {labelPositions.map((pos, idx) => (
                  <div
                    key={idx}
                    className="absolute rounded border-2 border-sumoPrimary bg-sumoPrimary/10 flex items-center justify-center"
                    style={{
                      left: `${pos.x}px`,
                      top: `${pos.y}px`,
                      width: `${pos.width}px`,
                      height: `${pos.height}px`
                    }}
                  >
                    <span className="text-[8px] font-bold text-sumoPrimary opacity-50">
                      {idx + 1}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
